import React, { useState } from 'react';
import styles from '../../pages/CheckoutPage.module.css';
import mobikwikLogo from '../../assets/wallet/mobikwik.svg';
import { CASHFREE_APP_PROVIDERS } from '../../utils/cashfreeUtils';

const WALLETS = [
  { id: 'phonepe',    name: 'PhonePe',      color: '#5f259f', initial: 'P',  logo: null },
  { id: 'amazonpay',  name: 'Amazon Pay',   color: '#ff9900', initial: 'A',  logo: null },
  { id: 'paytm',      name: 'Paytm',        color: '#00baf2', initial: 'P',  logo: null },
  { id: 'mobikwik',   name: 'MobiKwik',     color: '#00bcd4', initial: 'M',  logo: mobikwikLogo },
  { id: 'freecharge', name: 'FreeCharge',   color: '#e65100', initial: 'F',  logo: null },
];

// Fallback palette for wallets with no known brand color — used for
// liveWallets entries (Cashfree's eligibility API has no color field).
const FALLBACK_COLORS = ['#5f259f', '#ff9900', '#00baf2', '#00bcd4', '#e65100', '#98d13d', '#e40000', '#0f1b5f'];

// gateway prop added 2026-09-13 — Cashfree supports 9 wallet providers
// (gpay, phonepe, ola, paytm, amazon, airtel, freecharge, mobikwik, jio per
// its Order Pay API docs) vs this component's original 5, scoped to match
// Razorpay's picker — a real coverage gap found live on STG.
//
// liveWallets prop added 2026-09-17 — same reasoning as NetbankingSection.jsx's
// liveBanks: CASHFREE_POPULAR_WALLETS (the curated guess this replaces as
// default) can drift from what's actually enabled; liveWallets carries the
// REAL list from Cashfree's own eligibility API (cashfreeUtils.js::
// fetchCashfreeEligiblePaymentMethods) and is preferred when available.
// `nick` (not `code`) is used as the id — confirmed via a real sandbox
// eligibility response (2026-09-17) that Cashfree's payment_method.app.provider
// field is string-based, not numeric like netbanking_bank_code. Filtered
// through CASHFREE_APP_PROVIDERS: some nicks (ola_money, reliance_jio_money,
// airtel_money, amazon_pay) don't match the provider enum directly and need
// translation (handled in buildCashfreePaymentMethod); entries with NO known
// mapping (test_wallet, cashfree_pay — not part of the documented provider
// enum) are dropped here so we never offer a tile that would fail on tap.
//
// Update 2026-10-17: the CASHFREE_POPULAR_WALLETS fallback is gone (same
// reason as NetbankingSection.jsx — it offered wallets not enabled on the
// account while the live list was loading or came back empty). Cashfree now
// shows ONLY the live list; liveWalletsLoaded tells "still loading" apart
// from "loaded but nothing enabled". Razorpay's list is untouched.
function WalletSection({ amount, processing, onPay, enabledWallets = [], gateway = 'razorpay', liveWallets = [], liveWalletsLoaded = true }) {
  const [showAll, setShowAll] = useState(false);

  const knownLiveWallets = liveWallets.filter((w) => CASHFREE_APP_PROVIDERS[w.nick]);

  const cashfreeSource = knownLiveWallets.map((w, i) => ({
    id: w.nick,
    name: w.display || w.nick,
    color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    initial: (w.display || w.nick || '?').slice(0, 1).toUpperCase(),
    logo: null,
  }));

  const walletSource = gateway === 'cashfree' ? cashfreeSource : WALLETS;

  // enabledWallets (server-driven) is a Razorpay-specific concept —
  // Cashfree's list above (live or fallback) is shown as-is, no separate filtering.
  const visibleWallets = gateway !== 'cashfree' && enabledWallets.length > 0
    ? walletSource.filter((w) => enabledWallets.includes(w.id))
    : walletSource;

  const displayed = showAll ? visibleWallets : visibleWallets.slice(0, 3);

  if (gateway === 'cashfree' && visibleWallets.length === 0) {
    return (
      <div className={styles.bankList}>
        <p style={{ margin: 0, padding: '12px 4px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
          {liveWalletsLoaded
            ? 'Wallets are currently unavailable. Please use another payment method.'
            : 'Loading available wallets…'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.bankList}>
      {displayed.map((w) => (
        <button
          key={w.id}
          className={styles.bankListItem}
          onClick={() => onPay({ method: 'wallet', wallet: w.id })}
          disabled={processing}
        >
          <span
            className={styles.bankListAvatar}
            style={w.logo ? {} : { background: w.color }}
          >
            {w.logo
              ? <img src={w.logo} alt={w.name} style={{ width: 28, height: 28, objectFit: 'contain' }} />
              : w.initial}
          </span>
          <span className={styles.bankListName}>{w.name}</span>
          <span className={styles.bankListChevron}>›</span>
        </button>
      ))}

      {!showAll && visibleWallets.length > 3 && (
        <button className={styles.viewAllBtn} onClick={() => setShowAll(true)}>
          View All
        </button>
      )}
    </div>
  );
}

export default WalletSection;
